/**
 * Dialpad Integration Utilities
 *
 * Comprehensive utilities for building Dialpad app launch URLs for calls and SMS.
 * Provides robust phone number validation, URL generation, and error handling.
 * Integrates with FHIR resources for healthcare workflow support.
 *
 * Features:
 * - Phone number validation with E.164 format support
 * - Comprehensive error handling with branded types
 * - Direct phone number integration
 * - Type-safe URL generation
 * - Flexible phone number handling
 * - Automatic "from" number extraction from practitioner role
 *
 * @example
 * ```typescript
 * import { createPhoneCallLink } from './dialpad-utils';
 *
 * // Create call link from phone number with practitioner and patient IDs
 * const callUrl = createPhoneCallLink('+15551234567', 'practitioner-role-123', 'patient-456', '+15559876543', 'Follow-up call');
 * if (callUrl) {
 *   window.open(callUrl, '_blank');
 * }
 * ```
 *
 * Based on Dialpad App Launch URLs documentation:
 * https://developers.dialpad.com/docs/app-launch-urls
 */

// Types
import type { PractitionerRole } from "@medplum/fhirtypes";

// Utils
import {
  extractPractitionerRolePhoneNumber,
  toE164US,
} from "@benchmark/core/utils";
import { isValidPhoneNumber } from "@benchmark/phone";

/**
 * Branded type for validated phone numbers.
 * @agentic connections
 */
export type PhoneNumber = string & { readonly __brand: "PhoneNumber" };

/**
 * Branded type for validated Dialpad URLs.
 * @agentic connections
 */
export type DialpadUrl = string & { readonly __brand: "DialpadUrl" };

/**
 * Validates and brands a phone number string as a PhoneNumber type
 * This is a runtime check that ensures the phone number is valid
 *
 * For E.164 formatted numbers (starting with +), uses regex validation since
 * libphonenumber-js may reject valid E.164 test numbers like +15551234567
 *
 * For non-E.164 numbers, uses libphonenumber-js for proper validation
 *
 * @param phoneNumber - Phone number string to validate
 * @returns Validated PhoneNumber or null if invalid
 * @agentic connections
 */
export function validatePhoneNumber(
  phoneNumber: string | null,
): PhoneNumber | null {
  if (!phoneNumber) {
    return null;
  }

  // E.164 format: +[country code][number]
  // For US: +1XXXXXXXXXX (11 digits after +)
  const e164Regex = /^\+[1-9]\d{1,14}$/;

  // If already in E.164 format, validate with regex (backward compatible)
  if (phoneNumber.startsWith("+")) {
    if (!e164Regex.test(phoneNumber)) {
      return null;
    }
    return phoneNumber as PhoneNumber;
  }

  // For non-E.164 format, use libphonenumber-js for proper validation
  if (!isValidPhoneNumber(phoneNumber, "US")) {
    return null;
  }

  return phoneNumber as PhoneNumber;
}

/**
 * Error type for Dialpad utilities with code and context.
 * @agentic connections
 */
export class DialpadError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly context?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "DialpadError";
  }
}

/**
 * Options for building Dialpad call/SMS launch URLs.
 * @agentic connections
 */
export type DialpadLinkOptions = {
  /** Phone number to dial/text - must be valid E.164 format */
  phoneNumber: string;
  /** Practitioner's number to use as "from" number */
  fromNumber?: string;
  /** ID of the practitioner role placing the call */
  practitionerRoleId: string;
  /** ID of the patient related to this call */
  patientId: string;
  /** Always launch in mini mode */
  launchMinimode?: boolean;
  /** Require user confirmation before dialing */
  confirmPhone?: boolean;
  /** Custom data to include in the URL (will be URL encoded) */
  customData?: string;
  /** Show call dispositions in Dialpad */
  showDispositions?: boolean;
  /** Suppress CRM logging */
  suppressCrmLogging?: boolean;
};

/**
 * Enhanced Dialpad link options with validated phone numbers.
 * @agentic connections
 */
export type ValidatedDialpadLinkOptions = {
  /** Validated phone number */
  phoneNumber: PhoneNumber;
  /** Validated from number */
  fromNumber?: PhoneNumber;
} & Omit<DialpadLinkOptions, "phoneNumber" | "fromNumber">;

/**
 * Build Dialpad call launch URL
 *
 * Creates a Dialpad app launch URL for making calls with full parameter support.
 * Assumes phone numbers are already valid and formatted.
 *
 * @param options - Call configuration options
 * @param options.phoneNumber - Target phone number (must be valid E.164 format)
 * @param options.practitionerRoleId - ID of the practitioner role placing the call
 * @param options.patientId - ID of the patient related to this call
 * @param options.fromNumber - Practitioner's number to use as caller ID
 * @param options.launchMinimode - Launch Dialpad in mini mode
 * @param options.confirmPhone - Require user confirmation before dialing
 * @param options.customData - Custom data to include in the URL (max 1000 chars)
 * @param options.showDispositions - Show call dispositions in Dialpad
 * @param options.suppressCrmLogging - Suppress CRM logging
 * @returns Dialpad URL for call launch
 * @throws {DialpadError} When custom data is too long, practitionerRoleId is missing, or patientId is missing
 * @agentic connections
 *
 * @example
 * ```typescript
 * const url = buildDialpadCallUrl({
 *   phoneNumber: '+15551234567',
 *   practitionerRoleId: 'practitioner-role-123',
 *   patientId: 'patient-456',
 *   fromNumber: '+15559876543',
 *   launchMinimode: true,
 *   confirmPhone: true,
 *   customData: 'Patient follow-up call'
 * });
 * // Returns: 'dialpad://+15551234567?fromNumber=%2B15559876543&launchMinimode=1&confirmPhone=1&customData=Patient+follow-up+call+%7C+PractitionerRole_ID%3A+practitioner-role-123+%7C+Patient_ID%3A+patient-456'
 * ```
 */
export function buildDialpadCallUrl(options: DialpadLinkOptions): DialpadUrl {
  // Validate required IDs
  validateRequiredIds(options.practitionerRoleId, options.patientId);

  // Create enhanced custom data with practitioner and patient IDs
  const enhancedCustomData = options.customData
    ? `${options.customData} | PractitionerRole_ID: ${options.practitionerRoleId} | Patient_ID: ${options.patientId}`
    : `PractitionerRole_ID: ${options.practitionerRoleId} | Patient_ID: ${options.patientId}`;

  // Validate customData length (Dialpad has limits)
  if (enhancedCustomData.length > 1000) {
    throw new DialpadError(
      "Custom data exceeds maximum length of 1000 characters",
      "CUSTOM_DATA_TOO_LONG",
      { customDataLength: enhancedCustomData.length },
    );
  }

  const params = new URLSearchParams();

  // Add fromNumber parameter if provided
  if (options.fromNumber) {
    params.append("fromNumber", options.fromNumber);
  }

  if (options.launchMinimode) {
    params.append("launchMinimode", "1");
  }

  if (options.confirmPhone) {
    params.append("confirmPhone", "1");
  }

  // Always include the enhanced custom data with IDs
  params.append("customData", enhancedCustomData);

  if (options.showDispositions !== undefined) {
    params.append("showDispositions", options.showDispositions ? "1" : "0");
  }

  if (options.suppressCrmLogging) {
    params.append("crmLogging", "0");
  }

  const queryString = params.toString();
  const url = `dialpad://${options.phoneNumber}${queryString ? `?${queryString}` : ""}`;

  return url as DialpadUrl;
}

/**
 * Build validated Dialpad call URL with type safety.
 * @agentic connections
 */
export function buildValidatedDialpadCallUrl(
  options: ValidatedDialpadLinkOptions,
): DialpadUrl {
  // Validate required IDs
  validateRequiredIds(options.practitionerRoleId, options.patientId);

  // Create enhanced custom data with practitioner and patient IDs
  const enhancedCustomData = options.customData
    ? `${options.customData} | PractitionerRole_ID: ${options.practitionerRoleId} | Patient_ID: ${options.patientId}`
    : `PractitionerRole_ID: ${options.practitionerRoleId} | Patient_ID: ${options.patientId}`;

  // Validate customData length (Dialpad has limits)
  if (enhancedCustomData.length > 1000) {
    throw new DialpadError(
      "Custom data exceeds maximum length of 1000 characters",
      "CUSTOM_DATA_TOO_LONG",
      { customDataLength: enhancedCustomData.length },
    );
  }

  const params = new URLSearchParams();

  // Add fromNumber parameter if provided
  if (options.fromNumber) {
    params.append("fromNumber", options.fromNumber);
  }

  if (options.launchMinimode) {
    params.append("launchMinimode", "1");
  }

  if (options.confirmPhone) {
    params.append("confirmPhone", "1");
  }

  // Always include the enhanced custom data with IDs
  params.append("customData", enhancedCustomData);

  if (options.showDispositions !== undefined) {
    params.append("showDispositions", options.showDispositions ? "1" : "0");
  }

  if (options.suppressCrmLogging) {
    params.append("crmLogging", "0");
  }

  const queryString = params.toString();
  const url = `dialpad://${options.phoneNumber}${queryString ? `?${queryString}` : ""}`;

  return url as DialpadUrl;
}

/**
 * Options for creating a phone call link
 */
export type CreatePhoneCallLinkOptions = {
  /** Phone number to call (must be valid E.164 format) */
  phoneNumber: string;
  /** ID of the patient related to this call */
  patientId: string;
  /** Practitioner role resource (used to extract ID and from number) */
  practitionerRole: PractitionerRole;
  /** Optional practitioner's phone number to use as caller ID (overrides auto-extraction) */
  fromNumber?: string;
  /** Optional custom data to include in the call */
  customData?: string;
};

/**
 * Create a Dialpad call link from a phone number
 *
 * Creates a Dialpad call URL from a phone number string.
 * Assumes the phone number is already valid and formatted.
 * Automatically extracts the "from" number from practitionerRole if provided and fromNumber is not explicitly set.
 *
 * @param options - Call link options
 * @returns Dialpad URL for call
 *
 * @example
 * ```typescript
 * const callUrl = createPhoneCallLink({
 *   phoneNumber: '+15551234567',
 *   practitionerRoleId: 'practitioner-role-123',
 *   patientId: 'patient-456',
 *   customData: 'Follow-up call',
 *   practitionerRole,
 * });
 * window.open(callUrl, '_blank');
 * ```
 */
export function createPhoneCallLink(
  options: CreatePhoneCallLinkOptions,
): DialpadUrl {
  // Extract practitionerRoleId from practitionerRole
  const practitionerRoleId = options.practitionerRole.id;

  if (!practitionerRoleId) {
    throw new DialpadError(
      "PractitionerRole must have an id",
      "MISSING_PRACTITIONER_ROLE_ID",
      { practitionerRole: options.practitionerRole },
    );
  }

  // Auto-extract from number if not explicitly provided
  // Normalize to E.164 format for consistency
  let effectiveFromNumber = options.fromNumber;

  if (!effectiveFromNumber) {
    const extractedPhone = extractPractitionerRolePhoneNumber(
      options.practitionerRole,
    );
    if (extractedPhone) {
      effectiveFromNumber = toE164US(extractedPhone);
    }
  }

  const dialpadOptions: DialpadLinkOptions = {
    phoneNumber: options.phoneNumber,
    practitionerRoleId,
    patientId: options.patientId,
    fromNumber: effectiveFromNumber,
    customData: options.customData || `Phone Call - ${options.phoneNumber}`,
    launchMinimode: true,
    confirmPhone: true,
    showDispositions: true,
    suppressCrmLogging: false,
  };

  return buildDialpadCallUrl(dialpadOptions);
}

/**
 * Options for creating a validated Dialpad phone call link.
 * @agentic connections
 */
export type CreateValidatedPhoneCallLinkOptions = {
  /** Validated phone number to call */
  phoneNumber: PhoneNumber;
  /** ID of the patient related to this call */
  patientId: string;
  /** Practitioner role resource (used to extract ID and from number) */
  practitionerRole: PractitionerRole;
  /** Optional validated practitioner's phone number to use as caller ID (overrides auto-extraction) */
  fromNumber?: PhoneNumber;
  /** Optional custom data to include in the call */
  customData?: string;
};

/**
 * Create a validated call link from a phone number with type safety
 * Automatically extracts the "from" number from practitionerRole if provided and fromNumber is not explicitly set.
 * Validates extracted phone numbers to ensure type safety.
 *
 * @param options - Validated call link options
 * @returns Dialpad URL for call
 * @agentic connections
 */
export function createValidatedPhoneCallLink(
  options: CreateValidatedPhoneCallLinkOptions,
): DialpadUrl {
  // Extract practitionerRoleId from practitionerRole
  const practitionerRoleId = options.practitionerRole.id;

  if (!practitionerRoleId) {
    throw new DialpadError(
      "PractitionerRole must have an id",
      "MISSING_PRACTITIONER_ROLE_ID",
      { practitionerRole: options.practitionerRole },
    );
  }

  // Auto-extract from number if not explicitly provided
  // Normalize to E.164 and validate the extracted phone number to maintain type safety
  let effectiveFromNumber: PhoneNumber | undefined = options.fromNumber;

  if (!effectiveFromNumber) {
    const extractedPhone = extractPractitionerRolePhoneNumber(
      options.practitionerRole,
    );
    if (extractedPhone) {
      // Normalize to E.164 format before validation
      const normalizedPhone = toE164US(extractedPhone);
      effectiveFromNumber = validatePhoneNumber(normalizedPhone) || undefined;
    }
  }

  const dialpadOptions: ValidatedDialpadLinkOptions = {
    phoneNumber: options.phoneNumber,
    practitionerRoleId,
    patientId: options.patientId,
    fromNumber: effectiveFromNumber,
    customData: options.customData || `Phone Call - ${options.phoneNumber}`,
    launchMinimode: true,
    confirmPhone: true,
    showDispositions: true,
    suppressCrmLogging: false,
  };

  return buildValidatedDialpadCallUrl(dialpadOptions);
}

/**
 * Create a generic Dialpad launch URL
 * Opens the main Dialpad application without targeting a specific number
 */
export function createDialpadLaunchUrl(
  practitionerRoleId: string,
  patientId: string,
  customData?: string,
): DialpadUrl {
  // Validate required IDs
  validateRequiredIds(practitionerRoleId, patientId);

  // Create enhanced custom data with practitioner and patient IDs
  const enhancedCustomData = customData
    ? `${customData} | PractitionerRole_ID: ${practitionerRoleId} | Patient_ID: ${patientId}`
    : `PractitionerRole_ID: ${practitionerRoleId} | Patient_ID: ${patientId}`;

  // Validate customData length
  if (enhancedCustomData.length > 1000) {
    throw new DialpadError(
      "Custom data exceeds maximum length of 1000 characters",
      "CUSTOM_DATA_TOO_LONG",
      { customDataLength: enhancedCustomData.length },
    );
  }

  const params = new URLSearchParams();

  // Always include the enhanced custom data with IDs
  params.append("customData", enhancedCustomData);

  // Use default options
  params.append("launchMinimode", "1");
  params.append("confirmPhone", "1");

  const queryString = params.toString();
  const url = `dialpad://${queryString ? `?${queryString}` : ""}`;

  return url as DialpadUrl;
}

/**
 * Helper to create a generic Dialpad launch URL for phone context.
 * @agentic connections
 */
export function createPhoneDialpadLaunch(
  phoneNumber: string,
  practitionerRoleId: string,
  patientId: string,
  purpose?: string,
): DialpadUrl {
  const customData = `${purpose || "Phone Call"} - ${phoneNumber}`;

  return createDialpadLaunchUrl(practitionerRoleId, patientId, customData);
}

/** Validates practitionerRoleId and patientId for Dialpad operations. @agentic connections */
function validateRequiredIds(
  practitionerRoleId: string,
  patientId: string,
): void {
  if (!practitionerRoleId || practitionerRoleId.trim() === "") {
    throw new DialpadError(
      "PractitionerRole ID is required and cannot be empty",
      "MISSING_PRACTITIONER_ROLE_ID",
      { practitionerRoleId },
    );
  }

  if (!patientId || patientId.trim() === "") {
    throw new DialpadError(
      "Patient ID is required and cannot be empty",
      "MISSING_PATIENT_ID",
      { patientId },
    );
  }
}

/**
 * Check if Dialpad is available (browser with window.open).
 * @agentic connections
 */
export function isDialpadAvailable(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  // Check if we're in a browser environment
  return typeof window.open === "function";
}

/**
 * Open Dialpad URL in new tab with error handling.
 * @agentic connections
 */
export function openDialpadUrl(url: DialpadUrl): boolean {
  if (!isDialpadAvailable()) {
    throw new DialpadError(
      "Dialpad is not available in this environment",
      "DIALPAD_NOT_AVAILABLE",
      { url },
    );
  }

  try {
    // Attempt to open the Dialpad URL
    window.open(url, "_blank");
    return true;
  } catch (error) {
    throw new DialpadError(
      "Failed to open Dialpad URL",
      "DIALPAD_OPEN_FAILED",
      { url, error: error instanceof Error ? error.message : String(error) },
    );
  }
}
